import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { Loader2, FileText, BookOpen, Plug, Building2, Users, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { portalPath } from '../../lib/portal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/help`;

const DOC_META = {
  'agency-registration': { title: 'Agency Registration Guide', icon: Building2, audience: 'Government agency administrators' },
  'punchout': { title: 'Punchout Integration Guide', icon: Plug, audience: 'Procurement & IT integrators (cXML / OCI)' },
  'agency-admin-user-management': { title: 'Managing Users in Your Organisation', icon: Users, audience: 'Agency Administrators' },
};

const GovHelp = () => {
  const { slug = 'agency-registration' } = useParams();
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const meta = DOC_META[slug] || DOC_META['agency-registration'];
  const Icon = meta.icon;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    axios.get(`${API}/${slug}`, { responseType: 'text', transformResponse: (v) => v })
      .then((res) => { if (active) setMarkdown(res.data); })
      .catch((err) => { if (active) setError(err.response?.data?.detail || 'Could not load document'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  const download = () => {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="gov-help-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-blue-700" /> Help & Documentation
          </h1>
          <p className="text-sm text-slate-500">Operational manuals for the USKE Procurement Portal.</p>
        </div>
        <Button variant="outline" onClick={download} disabled={!markdown} data-testid="gov-help-download">
          <Download className="h-4 w-4 mr-1" /> Download .md
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Side nav */}
        <aside className="lg:col-span-1 space-y-2">
          {Object.entries(DOC_META).map(([s, m]) => {
            const SideIcon = m.icon;
            return (
              <NavLink
                key={s}
                to={portalPath(`/help/${s}`)}
                className={({ isActive }) =>
                  `block p-3 rounded border-2 transition ${isActive ? 'border-blue-700 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'}`
                }
                data-testid={`gov-help-nav-${s}`}
              >
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <SideIcon className="h-4 w-4" /> {m.title}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{m.audience}</div>
              </NavLink>
            );
          })}
        </aside>

        {/* Document */}
        <Card className="lg:col-span-3 border-2">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 mb-3">
              <Icon className="h-3.5 w-3.5" /> {meta.audience}
            </div>
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-blue-700" /></div>
            ) : error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
            ) : (
              <article className="prose prose-slate max-w-none prose-headings:text-slate-800 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-table:text-sm prose-code:text-[12px] prose-code:bg-slate-100 prose-code:px-1 prose-code:rounded prose-code:before:hidden prose-code:after:hidden prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-a:text-blue-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
              </article>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GovHelp;
